import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  memo,
} from 'react';
import t from 'components/i18n';
import { ThemeContext } from 'styled-components';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { API_SUPERVISOR_URL, KONG_CLIENT_ID } from 'config/constants';
import NoContentTab from 'components/NoContentTab';
import TableComponent from 'components/Table';
import Pagination from 'components/Pagination';
import Icon from 'components/Icon';
import InsuranceDetails from './InsuranceDetails';
import { getInsurancePolicyList } from './service';
import { ContentsWrapper, Container } from './Documentation.styled';
import { creationMapper, statusMapper } from 'utils/mapper';

function Insurance({ responsive }) {
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(null);
  const [data, setData] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [
    displayInsuranceDetails,
    setDisplayInsuranceDetails,
  ] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [bodyRequest, setBodyRequest] = useState({
    PageNumber: 1,
    PageSize: 20,
    SupervisorCode: '',
    VendorCode: '',
    AnimalType: '',
    Register: '',
  });

  const { isMobile } = responsive;

  const themeContext = useContext(ThemeContext);
  const user = useSelector(state => state.user);

  let bgColor = '';
  let iconClass = '';
  let displayDetails = false;

  const getInurance = useCallback(async e => {
    setIsContentLoading(true);
    try {
      const response = await getInsurancePolicyList(e);
      setData(response.data);
    } catch (error) {
      console.error(error);
    }
    setIsContentLoading(false);
  }, []);

  const getFilterByLabel = useCallback(
    label => activeFilters.find(filter => label === filter.label),
    [activeFilters],
  );

  const getFilters = useMemo(() => {
    return [
      {
        label: t('documentation.insurance.animal-type.label'),
        items: [
          {
            txt: t('documentation.insurance.animal-type.options.chicken'),
          },
          {
            txt: t('documentation.insurance.animal-type.options.turkey'),
          },
          {
            txt: t('documentation.insurance.animal-type.options.swine'),
          },
        ],
      },
      {
        label: t('documentation.insurance.status-type.label'),
        items: [
          {
            txt: t('documentation.insurance.status-type.options.complete'),
            val: true,
          },
          {
            txt: t('documentation.insurance.status-type.options.incomplete'),
            val: false,
          },
        ],
      },
    ];
  }, []);

  useEffect(() => {
    if (activeFilters) {
      // new search
      setCurrentPage(1);

      const supervisor = getFilterByLabel(t('documentation.insurance.extensionist-name'));
      const producer = getFilterByLabel(t('documentation.insurance.integrated-name'));
      const animalType = getFilterByLabel(t('documentation.insurance.animal-type.label'));
      const register = getFilterByLabel(t('documentation.insurance.status-type.label'));

      setBodyRequest(oldBodyRequest => ({
        ...oldBodyRequest,
        SupervisorCode: supervisor ? supervisor.id : '',
        VendorCode: producer ? producer.code : '',
        AnimalType: animalType
          ? animalType.txt.replace('í', 'i').replace('Todos', '')
          : '',
        Register: register ? register.val : '',
      }));
    }
  }, [activeFilters, getFilterByLabel]);

  useEffect(() => {
    if (selectedInsurance) {
      setDisplayInsuranceDetails(true);
    }
  }, [selectedInsurance]);

  const tableData = useMemo(
    () =>
      data &&
      data.itens &&
      data.itens.length > 0 &&
      data.itens.map((e, index) => {
        if (e.register) {
          switch (e.register.toLowerCase()) {
            case 'realizado':
              bgColor = 'green';
              iconClass = 'icon icon-check-circle';
              displayDetails = true;
              break;
            default:
              bgColor = 'red';
              iconClass = 'icon icon-close-circle';
          }
        }

        return {
          0: (
            <div
              index={index}
              className={isMobile ? 'column mobile' : 'column'}
            >
              <p className="small caps">{t('documentation.insurance.table.code', { value: e.vendorCode })}</p>
              <p className="strong">{e.vendorName}</p>
              <p className="small">
                {t('documentation.insurance.table.extensionist', { value: e.supervisorName })}
              </p>
            </div>
          ),
          1: `${creationMapper(e.animalType)}`,
          2: (
            <div className={bgColor}>
              <span className={iconClass} /> {t(statusMapper(e.register))}
            </div>
          ),
          3: `${
            e.registerDate
              ? moment
                  .utc(e.registerDate)
                  .local()
                  .format('DD ̸̸  MM ̸̸  YYYY - HH:mm')
              : '-'
          }`,
          4: e.register === 'Realizado' && (
            <button
              className="open-details"
              type="button"
              onClick={() => setSelectedInsurance(e)}
            >
              <Icon name="icon-ellipsis-v" />
            </button>
          ),
        };
      }),
    [data],
  );

  const getPagination = useMemo(
    () =>
      data &&
      Number(data.totalPages) > 0 && (
        <Pagination
          totalPages={data.totalPages}
          pagePadding={isMobile ? 0.75 : 2}
          currentPage={currentPage}
          onChangePage={x => setCurrentPage(x.e)}
        />
      ),
    [currentPage, data, isMobile],
  );

  useEffect(() => {
    if (currentPage) {
      getInurance({ ...bodyRequest, PageNumber: currentPage });
    }
  }, [currentPage, bodyRequest, getInurance]);

  const renderTable = useMemo(
    () => (
      <TableComponent
        loading={isContentLoading}
        filter={{
          activeFilters,
          setActiveFilters,
          filters: getFilters,
        }}
        downloadLink={`${API_SUPERVISOR_URL}/Producer/DownloadInsurancePolicyList?pageSize=20&pageNumber=${bodyRequest.PageNumber}&VendorCode=${bodyRequest.VendorCode}&SupervisorCode=${bodyRequest.SupervisorCode}&AnimalType=${bodyRequest.AnimalType}&Register=${bodyRequest.Register}&token=${user.token}&client_id=${KONG_CLIENT_ID}`}
        downloadLabel={t('documentation.insurance.table.download.label')}
        titleColor={themeContext.styles.palette.colorPrimary}
        rowSize="default"
        header={[
          {
            width: 40,
            title: t('documentation.insurance.table.header.profile'),
          },
          {
            width: 20,
            title: t('documentation.insurance.table.header.animal-type'),
          },
          {
            width: 20,
            title: t('documentation.insurance.table.header.registration'),
          },
          {
            width: 14,
            title: t('documentation.insurance.table.header.date'),
          },
          {
            width: 6,
            title: '',
          },
        ]}
        mobileHeader={t('documentation.insurance.table.header.profile')}
        data={tableData}
        width="25%"
        isMobile={isMobile}
      />
    ),
    [
      activeFilters,
      bodyRequest,
      getFilters,
      isContentLoading,
      isMobile,
      tableData,
      themeContext,
      user,
    ],
  );

  return (
    <ContentsWrapper>
      <Container style={{ marginBottom: 30 }}>
        {!isContentLoading && !data && (
          <NoContentTab
            icon="icon-clipboard-user"
            onButtonClick={() => {}}
          >
            <p>
              {t('insurancePolicy.empty.title')} <br />{' '}
              <span className="lighter">
                {t('insurancePolicy.empty.subtitle')}
              </span>
            </p>
          </NoContentTab>
        )}

        {renderTable}
      </Container>

      {getPagination}

      {selectedInsurance && (
        <InsuranceDetails
          title={t('documentation.insurance.details-title')}
          openSideBox={displayInsuranceDetails}
          setOpenSideBox={setDisplayInsuranceDetails}
          startDate={selectedInsurance.startDate}
          endDate={selectedInsurance.endDate}
          files={selectedInsurance.files}
          container={selectedInsurance.containerName}
        />
      )}
    </ContentsWrapper>
  );
}

export default memo(Insurance);
